import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

// Import refactored components
import { 
  CertificateTemplate, 
  QuizDialog, 
  VideoDialog, 
  AssignmentDialog, 
  AcademyStats 
} from "@/components/academy";

// Import course data
import { ELNEC_MODULES, TRAINING_COURSES, ACHIEVEMENTS } from "@/data/academyCourseData";

import {
  GraduationCap, Play, Clock, CheckCircle2, Star, Award, BookOpen, Search,
  TrendingUp, Video, Target, Lock, Trophy, Download, Printer, BadgeCheck,
  ClipboardCheck, RefreshCw, BarChart3, PieChart, Bell, BellRing, Timer,
  UserPlus, FileText, ScrollText
} from "lucide-react";

export default function HospiceAcademy() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const certificateRef = useRef(null);

  // URL param handling for tab navigation
  const tabParam = searchParams.get('tab');
  const initialTab = tabParam === 'active' || tabParam === 'due' || tabParam === 'overdue' || tabParam === 'completed' 
    ? 'assignments' : 'courses';

  // UI State
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState(initialTab);
  const [assignmentFilter, setAssignmentFilter] = useState(tabParam || 'all');

  // Progress State
  const [completedCourses, setCompletedCourses] = useState([]);
  const [completionDates, setCompletionDates] = useState({});
  const [quizAttempts, setQuizAttempts] = useState({});
  const [videoWatched, setVideoWatched] = useState({});
  const [streak, setStreak] = useState(0);
  const [cahpsData, setCahpsData] = useState(null);

  // Dialog State
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [showQuizDialog, setShowQuizDialog] = useState(false);
  const [showCertificateDialog, setShowCertificateDialog] = useState(false);
  const [certificateCourse, setCertificateCourse] = useState(null);

  // Admin State
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [reminderStatus, setReminderStatus] = useState(null);
  const [processingReminders, setProcessingReminders] = useState(false);

  // Auth checks
  const allowedRoles = ["staff", "administrator", "admin"];
  const userRole = user?.role?.toLowerCase() || "";
  const isAuthorized = user && allowedRoles.includes(userRole);
  const isAdmin = userRole === "administrator" || userRole === "admin";

  // Load data on mount
  useEffect(() => {
    if (authLoading) return;
    if (!user || !allowedRoles.includes(user?.role?.toLowerCase())) {
      navigate("/", { replace: true });
      return;
    }

    loadProgress();
    api.get("/cahps-scores").then(res => setCahpsData(res.data)).catch(console.error);

    if (isAdmin) {
      api.get("/training/staff-for-assignment").then(res => setStaffList(res.data.staff || [])).catch(console.error);
      api.get("/training/disciplines").then(res => setDisciplines(res.data.disciplines || [])).catch(console.error);
      api.get("/training/assignments").then(res => setAssignments(res.data.assignments || [])).catch(console.error);
      api.get("/training/reminder-status").then(res => setReminderStatus(res.data)).catch(console.error);
    }
  }, [user, navigate, authLoading]);

  const loadProgress = async () => {
    try {
      const response = await api.get("/academy/progress");
      const progress = response.data;
      if (progress) {
        setCompletedCourses(progress.completed_courses || []);
        setCompletionDates(progress.completion_dates || {});
        setQuizAttempts(progress.quiz_attempts || {});
        setVideoWatched((progress.videos_watched || []).reduce((acc, id) => ({ ...acc, [id]: true }), {}));
        setStreak(progress.streak || 0);
      }
    } catch (error) {
      console.error("Failed to load progress:", error);
    }
  };

  // Course actions
  const markVideoWatched = async (courseId) => {
    if (!videoWatched[courseId]) {
      setVideoWatched({ ...videoWatched, [courseId]: true });
      try {
        await api.post("/academy/progress/video-watched", { course_id: courseId });
      } catch (error) {
        console.error("Failed to save video watched:", error);
      }
    }
  };

  const markCourseComplete = async (courseId, score) => {
    if (!completedCourses.includes(courseId)) {
      setCompletedCourses([...completedCourses, courseId]);
      setCompletionDates({ ...completionDates, [courseId]: new Date().toISOString() });

      const course = allCourses.find(c => c.id === courseId);
      try {
        const response = await api.post("/academy/progress/complete-course", {
          course_id: courseId,
          quiz_score: score,
          quiz_passed: score >= 80,
          ceu_credits: course?.ceuCredits || 0
        });
        if (response.data?.progress?.streak) {
          setStreak(response.data.progress.streak);
        }
      } catch (error) {
        console.error("Failed to save completion:", error);
      }
    }
  };

  const openCourse = (course) => {
    setSelectedCourse(course);
    setShowVideoDialog(true);
  };

  const startQuiz = (course) => {
    setSelectedCourse(course);
    setShowQuizDialog(true);
  };

  const openCertificate = (course) => {
    setCertificateCourse(course);
    setShowCertificateDialog(true);
  };

  const printCertificate = () => {
    const printWindow = window.open('', '_blank');
    const certificateHTML = certificateRef.current?.innerHTML;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Certificate - ${certificateCourse?.title}</title>
          <style>
            body { font-family: Georgia, serif; margin: 40px; }
            .text-center { text-align: center; }
            .text-primary { color: #0ea5e9; }
            .font-bold { font-weight: bold; }
          </style>
        </head>
        <body>${certificateHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Admin actions
  const handleCreateAssignment = async (data) => {
    setAssignmentLoading(true);
    try {
      const response = await api.post("/training/assignments", {
        user_id: data.userId,
        discipline: data.discipline,
        due_date: data.dueDate,
        notes: data.notes
      });
      if (response.data.success) {
        const assignmentsRes = await api.get("/training/assignments");
        setAssignments(assignmentsRes.data.assignments || []);
        toast.success("Training Assigned", { description: "Staff member has been assigned training." });
        setShowAssignDialog(false);
      }
    } catch (error) {
      toast.error("Assignment Failed", { description: "Failed to create assignment." });
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleProcessReminders = async () => {
    setProcessingReminders(true);
    try {
      const response = await api.post("/training/process-reminders");
      if (response.data.success) {
        const statusRes = await api.get("/training/reminder-status");
        setReminderStatus(statusRes.data);
        toast.success("Reminders Processed", { description: `Sent ${response.data.total_reminders_sent} reminders.` });
      }
    } catch (error) {
      toast.error("Failed to process reminders");
    } finally {
      setProcessingReminders(false);
    }
  };

  // Filter assignments based on selected filter
  const getFilteredAssignments = () => {
    if (assignmentFilter === 'all') return assignments;
    if (assignmentFilter === 'active') return assignments.filter(a => a.status === 'active');
    if (assignmentFilter === 'completed') return assignments.filter(a => a.status === 'completed');
    if (assignmentFilter === 'due') {
      const weekFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
      return assignments.filter(a => {
        if (!a.due_date || a.status === 'completed') return false;
        const dueDate = new Date(a.due_date);
        return dueDate <= weekFromNow && dueDate >= new Date();
      });
    }
    if (assignmentFilter === 'overdue') {
      return assignments.filter(a => {
        if (!a.due_date || a.status === 'completed') return false;
        return new Date(a.due_date) < new Date();
      });
    }
    return assignments;
  };

  // Quiz helpers
  const isQuizPassed = (courseId) => (quizAttempts[courseId] || []).some(a => a.passed);
  const getBestScore = (courseId) => {
    const attempts = quizAttempts[courseId] || [];
    return attempts.length > 0 ? Math.max(...attempts.map(a => a.score)) : null;
  };

  // Computed values
  const allCourses = Object.values(TRAINING_COURSES).flatMap(cat => cat.courses);
  const totalCourses = allCourses.length;
  const completedCount = completedCourses.length;
  const requiredCourses = allCourses.filter(c => c.required);
  const completedRequired = requiredCourses.filter(c => completedCourses.includes(c.id)).length;
  const cahpsRelatedCourses = allCourses.filter(c => c.cahpsRelated);
  const completedCAHPS = cahpsRelatedCourses.filter(c => completedCourses.includes(c.id)).length;
  const elnecCourses = TRAINING_COURSES.elnec_core?.courses || [];
  const completedELNEC = elnecCourses.filter(c => completedCourses.includes(c.id)).length;
  const totalCEUsEarned = allCourses.filter(c => completedCourses.includes(c.id)).reduce((sum, c) => sum + (c.ceuCredits || 0), 0);
  const totalCEUsAvailable = allCourses.reduce((sum, c) => sum + (c.ceuCredits || 0), 0);

  const earnedAchievements = ACHIEVEMENTS.filter(achievement => {
    if (achievement.id === "first_course") return completedCount >= 1;
    if (achievement.id === "elnec_complete") return completedELNEC >= 6;
    if (achievement.id === "ceu_milestone") return totalCEUsEarned >= 10;
    if (achievement.id === "ceu_master") return totalCEUsEarned >= 25;
    if (achievement.id === "all_required") return completedRequired >= requiredCourses.length;
    if (achievement.id === "academy_graduate") return completedCount >= totalCourses;
    const trackMap = {
      pain_expert: "pain_management", timely_master: "timely_response",
      communicator: "communication", fundamentals: "fundamentals",
      rncm_certified: "rncm_track", wound_specialist: "wound_care",
      aide_certified: "aide_track", sw_certified: "social_work",
      chaplain_certified: "chaplaincy", tod_certified: "time_of_death"
    };
    if (trackMap[achievement.id]) {
      return TRAINING_COURSES[trackMap[achievement.id]]?.courses?.every(c => completedCourses.includes(c.id));
    }
    return false;
  });

  // Filter courses
  const filteredCourses = activeCategory === "all" ? allCourses : TRAINING_COURSES[activeCategory]?.courses || [];
  const searchedCourses = filteredCourses.filter(course =>
    course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    course.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Loading and auth states
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t('academy.accessRestricted', 'Access restricted to staff and administrators.')}</p>
          <Button className="mt-4" onClick={() => navigate("/")}>{t('common.goHome', 'Go Home')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="hospice-academy-page">
      {/* Hero Stats Header */}
      <AcademyStats
        totalCEUsEarned={totalCEUsEarned}
        totalCEUsAvailable={totalCEUsAvailable}
        streak={streak}
        completedCount={completedCount}
        totalCourses={totalCourses}
        completedRequired={completedRequired}
        requiredCount={requiredCourses.length}
        completedELNEC={completedELNEC}
        completedCAHPS={completedCAHPS}
        cahpsTotal={cahpsRelatedCourses.length}
        earnedAchievements={earnedAchievements.length}
        totalAchievements={ACHIEVEMENTS.length}
      />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid lg:grid-cols-4">
            <TabsTrigger value="courses"><Video className="w-4 h-4 mr-2" />{t('academy.tabs.courses', 'Courses')}</TabsTrigger>
            <TabsTrigger value="certificates"><FileText className="w-4 h-4 mr-2" />{t('academy.tabs.certificates', 'My Certificates')}</TabsTrigger>
            <TabsTrigger value="achievements"><Trophy className="w-4 h-4 mr-2" />{t('academy.tabs.achievements', 'Achievements')}</TabsTrigger>
            {isAdmin && <TabsTrigger value="assignments"><ClipboardCheck className="w-4 h-4 mr-2" />{t('academy.tabs.assignments', 'Assignments')}</TabsTrigger>}
            {isAdmin && <TabsTrigger value="analytics"><BarChart3 className="w-4 h-4 mr-2" />{t('academy.tabs.admin', 'Admin')}</TabsTrigger>}
          </TabsList>

          {/* Courses Tab */}
          <TabsContent value="courses" className="space-y-6">
            {/* ELNEC Banner */}
            <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
              <CardContent className="py-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg"><ScrollText className="w-6 h-6 text-indigo-600" /></div>
                    <div>
                      <h3 className="font-semibold text-indigo-900">{t('academy.elnec.title', 'ELNEC Core Curriculum')}</h3>
                      <p className="text-sm text-indigo-700">{t('academy.elnec.description', 'End-of-Life Nursing Education Consortium - 8.0 CEU Credits')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-indigo-600">{completedELNEC}/6</p>
                      <p className="text-xs text-indigo-500">{t('academy.modules', 'Modules')}</p>
                    </div>
                    <Button variant={completedELNEC === 6 ? "default" : "outline"} onClick={() => setActiveCategory("elnec_core")}>
                      {completedELNEC === 6 ? t('academy.completed', '✓ Completed') : t('academy.startElnec', 'Start ELNEC')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder={t('academy.searchCourses', 'Search courses...')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
              </div>
              <div className="flex gap-2 overflow-x-auto pb-2">
                <Button variant={activeCategory === "all" ? "default" : "outline"} size="sm" onClick={() => setActiveCategory("all")}>{t('common.all', 'All')}</Button>
                {Object.entries(TRAINING_COURSES).map(([key, category]) => {
                  const Icon = category.icon;
                  return (
                    <Button key={key} variant={activeCategory === key ? "default" : "outline"} size="sm" onClick={() => setActiveCategory(key)} className="whitespace-nowrap">
                      <Icon className="w-4 h-4 mr-1" />{category.title.replace("ELNEC Core Curriculum", "ELNEC").substring(0, 20)}
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Course Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {searchedCourses.map((course) => {
                const isCompleted = completedCourses.includes(course.id);
                return (
                  <Card key={course.id} className={`overflow-hidden transition-all hover:shadow-lg ${isCompleted ? 'border-green-200 bg-green-50/30' : course.elnecModule ? 'border-indigo-200' : ''}`}>
                    <div className="relative aspect-video bg-gradient-to-br from-slate-800 to-slate-900 cursor-pointer group" onClick={() => openCourse(course)}>
                      <img src={`https://img.youtube.com/vi/${course.youtubeId}/mqdefault.jpg`} alt={course.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Play className="w-6 h-6 text-primary ml-1" />
                        </div>
                      </div>
                      {isCompleted && <div className="absolute top-2 right-2 bg-green-500 text-white p-1 rounded-full"><CheckCircle2 className="w-4 h-4" /></div>}
                      <div className="absolute top-2 left-2 flex gap-1">
                        {course.required && !isCompleted && <Badge className="bg-red-500">{t('academy.required', 'Required')}</Badge>}
                        {course.elnecModule && <Badge className="bg-indigo-600">ELNEC {course.elnecModule}</Badge>}
                        {course.cahpsRelated && <Badge className="bg-orange-500">CAHPS</Badge>}
                      </div>
                      <div className="absolute bottom-2 right-2">
                        <Badge variant="secondary" className="bg-white/90 text-foreground"><Award className="w-3 h-3 mr-1" />{course.ceuCredits} CEU</Badge>
                      </div>
                    </div>
                    <CardContent className="pt-4">
                      <h3 className="font-medium text-sm line-clamp-2 mb-2">{course.title}</h3>
                      <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{course.description}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{course.duration}</span>
                        <Badge variant="outline" className="text-xs">{course.level}</Badge>
                        {getBestScore(course.id) !== null && (
                          <Badge variant="outline" className={`text-xs ${isQuizPassed(course.id) ? 'border-green-500 text-green-600' : 'border-orange-500 text-orange-600'}`}>
                            <ClipboardCheck className="w-3 h-3 mr-1" />{getBestScore(course.id)}%
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {isCompleted ? (
                          <>
                            <Button className="flex-1" size="sm" variant="outline" onClick={() => openCourse(course)}><Play className="w-4 h-4 mr-1" /> {t('academy.review', 'Review')}</Button>
                            <Button size="sm" variant="outline" onClick={() => openCertificate(course)}><FileText className="w-4 h-4" /></Button>
                          </>
                        ) : videoWatched[course.id] ? (
                          <>
                            <Button className="flex-1" size="sm" onClick={() => startQuiz(course)}><ClipboardCheck className="w-4 h-4 mr-1" /> {t('academy.takeQuiz', 'Take Quiz')}</Button>
                            <Button size="sm" variant="outline" onClick={() => openCourse(course)}><Play className="w-4 h-4" /></Button>
                          </>
                        ) : (
                          <Button className="flex-1" size="sm" onClick={() => openCourse(course)}><Play className="w-4 h-4 mr-1" /> {t('academy.watchVideo', 'Watch Video')}</Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Certificates Tab */}
          <TabsContent value="certificates" className="space-y-6">
            <div className="text-center py-4">
              <h2 className="text-2xl font-bold mb-2">{t('academy.certificates.title', 'My Certificates & CEU Transcript')}</h2>
              <p className="text-muted-foreground">{t('academy.certificates.subtitle', 'Download or print your completion certificates')}</p>
            </div>

            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="py-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-primary/10 rounded-xl"><Award className="w-8 h-8 text-primary" /></div>
                    <div>
                      <h3 className="text-xl font-bold">{totalCEUsEarned.toFixed(1)} {t('academy.ceuCreditsEarned', 'CEU Credits Earned')}</h3>
                      <p className="text-muted-foreground">{t('academy.ofTotalAvailable', 'of {{total}} total available', { total: totalCEUsAvailable.toFixed(1) })}</p>
                    </div>
                  </div>
                  <Progress value={(totalCEUsEarned / totalCEUsAvailable) * 100} className="w-48 h-3" />
                </div>
              </CardContent>
            </Card>

            {completedELNEC === 6 && (
              <Card className="border-indigo-300 bg-gradient-to-r from-indigo-50 to-purple-50">
                <CardContent className="py-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-indigo-100 rounded-xl"><ScrollText className="w-8 h-8 text-indigo-600" /></div>
                      <div>
                        <h3 className="text-lg font-bold text-indigo-900">{t('academy.elnecCertificate', 'ELNEC Core Completion Certificate')}</h3>
                        <p className="text-indigo-700">{t('academy.elnecCertDesc', 'End-of-Life Nursing Education Consortium • 8.0 CEU Credits')}</p>
                      </div>
                    </div>
                    <Button onClick={() => openCertificate({ title: "ELNEC Core Curriculum - Complete", ceuCredits: 8.0, isELNEC: true, id: "elnec-complete" })}>
                      <Download className="w-4 h-4 mr-2" />{t('academy.downloadCertificate', 'Download Certificate')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">{t('academy.individualCertificates', 'Individual Course Certificates')}</h3>
              {completedCourses.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <GraduationCap className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">{t('academy.completeToCertify', 'Complete courses to earn certificates')}</p>
                    <Button className="mt-4" onClick={() => setActiveTab("courses")}>{t('academy.browseCourses', 'Browse Courses')}</Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {allCourses.filter(c => completedCourses.includes(c.id)).map(course => (
                    <Card key={course.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {course.elnecModule && <Badge className="bg-indigo-100 text-indigo-700">ELNEC {course.elnecModule}</Badge>}
                              <Badge variant="outline">{course.ceuCredits} CEU</Badge>
                            </div>
                            <h4 className="font-medium">{course.title}</h4>
                            <p className="text-xs text-muted-foreground">
                              {t('academy.completedOn', 'Completed')}: {completionDates[course.id] ? new Date(completionDates[course.id]).toLocaleDateString() : "N/A"}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openCertificate(course)}><Printer className="w-4 h-4" /></Button>
                            <Button size="sm" variant="outline" onClick={() => openCertificate(course)}><Download className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Achievements Tab */}
          <TabsContent value="achievements" className="space-y-6">
            <div className="text-center py-4">
              <h2 className="text-2xl font-bold mb-2">{t('academy.achievements.title', 'Achievements & Badges')}</h2>
              <p className="text-muted-foreground">{t('academy.achievements.subtitle', '{{earned}} of {{total}} badges earned', { earned: earnedAchievements.length, total: ACHIEVEMENTS.length })}</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ACHIEVEMENTS.map(achievement => {
                const isEarned = earnedAchievements.some(a => a.id === achievement.id);
                return (
                  <Card key={achievement.id} className={`transition-all ${isEarned ? 'border-yellow-300 bg-yellow-50' : 'opacity-60'}`}>
                    <CardContent className="py-6 text-center">
                      <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${isEarned ? 'bg-yellow-100' : 'bg-muted'}`}>
                        <Trophy className={`w-8 h-8 ${isEarned ? 'text-yellow-600' : 'text-muted-foreground'}`} />
                      </div>
                      <h3 className={`font-bold mb-1 ${isEarned ? 'text-yellow-800' : 'text-muted-foreground'}`}>{achievement.title}</h3>
                      <p className="text-sm text-muted-foreground">{achievement.description}</p>
                      {isEarned && <Badge className="mt-3 bg-yellow-500"><CheckCircle2 className="w-3 h-3 mr-1" />{t('academy.earned', 'Earned')}</Badge>}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Assignments Tab - Admin Only */}
          {isAdmin && (
            <TabsContent value="assignments" className="space-y-6">
              <Card className="border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg"><ClipboardCheck className="w-6 h-6 text-blue-600" /></div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{t('academy.assignments.title', 'Training Assignments')}</h3>
                        <p className="text-sm text-slate-600">{t('academy.assignments.subtitle', 'Monitor and manage staff training progress')}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <select 
                        className="px-3 py-2 border rounded-md text-sm"
                        value={assignmentFilter}
                        onChange={(e) => setAssignmentFilter(e.target.value)}
                        data-testid="assignment-filter"
                      >
                        <option value="all">{t('academy.assignments.filter.all', 'All Assignments')}</option>
                        <option value="active">{t('academy.assignments.filter.active', 'Active (Outstanding)')}</option>
                        <option value="due">{t('academy.assignments.filter.due', 'Due Soon')}</option>
                        <option value="overdue">{t('academy.assignments.filter.overdue', 'Overdue')}</option>
                        <option value="completed">{t('academy.assignments.filter.completed', 'Completed')}</option>
                      </select>
                      <Button variant="outline" onClick={handleProcessReminders} disabled={processingReminders}>
                        <Bell className="w-4 h-4 mr-2" />{processingReminders ? t('academy.processing', 'Processing...') : t('academy.sendReminders', 'Send Reminders')}
                      </Button>
                      <Button onClick={() => setShowAssignDialog(true)}>
                        <UserPlus className="w-4 h-4 mr-2" />{t('academy.assignTraining', 'Assign Training')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Assignment Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card 
                  className={`cursor-pointer transition-shadow hover:shadow-lg ${assignmentFilter === 'active' ? 'ring-2 ring-emerald-500' : ''}`}
                  onClick={() => setAssignmentFilter('active')}
                >
                  <CardContent className="pt-6 text-center">
                    <GraduationCap className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
                    <p className="text-3xl font-bold text-emerald-700">{assignments.filter(a => a.status === 'active').length}</p>
                    <p className="text-sm text-muted-foreground">{t('academy.stats.active', 'Active/Outstanding')}</p>
                  </CardContent>
                </Card>
                <Card 
                  className={`cursor-pointer transition-shadow hover:shadow-lg ${assignmentFilter === 'due' ? 'ring-2 ring-amber-500' : ''}`}
                  onClick={() => setAssignmentFilter('due')}
                >
                  <CardContent className="pt-6 text-center">
                    <Timer className="w-8 h-8 mx-auto text-amber-600 mb-2" />
                    <p className="text-3xl font-bold text-amber-700">{reminderStatus?.due_soon_count || 0}</p>
                    <p className="text-sm text-muted-foreground">{t('academy.stats.dueSoon', 'Due Soon')}</p>
                  </CardContent>
                </Card>
                <Card 
                  className={`cursor-pointer transition-shadow hover:shadow-lg ${assignmentFilter === 'overdue' ? 'ring-2 ring-red-500' : ''}`}
                  onClick={() => setAssignmentFilter('overdue')}
                >
                  <CardContent className="pt-6 text-center">
                    <BellRing className="w-8 h-8 mx-auto text-red-600 mb-2" />
                    <p className="text-3xl font-bold text-red-700">{reminderStatus?.overdue_count || 0}</p>
                    <p className="text-sm text-muted-foreground">{t('academy.stats.overdue', 'Overdue')}</p>
                  </CardContent>
                </Card>
                <Card 
                  className={`cursor-pointer transition-shadow hover:shadow-lg ${assignmentFilter === 'completed' ? 'ring-2 ring-purple-500' : ''}`}
                  onClick={() => setAssignmentFilter('completed')}
                >
                  <CardContent className="pt-6 text-center">
                    <CheckCircle2 className="w-8 h-8 mx-auto text-purple-600 mb-2" />
                    <p className="text-3xl font-bold text-purple-700">{assignments.filter(a => a.status === 'completed').length}</p>
                    <p className="text-sm text-muted-foreground">{t('academy.stats.completed', 'Completed')}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Assignments List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5" />
                    {assignmentFilter === 'all' ? t('common.all', 'All') : assignmentFilter.charAt(0).toUpperCase() + assignmentFilter.slice(1)} {t('academy.assignments.label', 'Assignments')}
                    <Badge variant="secondary">{getFilteredAssignments().length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {getFilteredAssignments().length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <ClipboardCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>{t('academy.assignments.none', 'No {{filter}} assignments found', { filter: assignmentFilter === 'all' ? '' : assignmentFilter })}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {getFilteredAssignments().map((assignment, idx) => {
                        const staff = staffList.find(s => s.id === assignment.user_id);
                        const course = allCourses.find(c => c.id === assignment.course_id);
                        const isOverdue = assignment.due_date && new Date(assignment.due_date) < new Date() && assignment.status !== 'completed';
                        const isDueSoon = assignment.due_date && new Date(assignment.due_date) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) && !isOverdue && assignment.status !== 'completed';
                        
                        return (
                          <div 
                            key={idx}
                            className={`flex items-center justify-between p-4 rounded-lg border ${
                              isOverdue ? 'bg-red-50 border-red-200' :
                              isDueSoon ? 'bg-amber-50 border-amber-200' :
                              assignment.status === 'completed' ? 'bg-green-50 border-green-200' :
                              'bg-slate-50 border-slate-200'
                            }`}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`p-2 rounded-lg ${
                                isOverdue ? 'bg-red-100' :
                                isDueSoon ? 'bg-amber-100' :
                                assignment.status === 'completed' ? 'bg-green-100' :
                                'bg-blue-100'
                              }`}>
                                {assignment.status === 'completed' ? (
                                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                                ) : isOverdue ? (
                                  <BellRing className="w-5 h-5 text-red-600" />
                                ) : (
                                  <GraduationCap className="w-5 h-5 text-blue-600" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium">{staff?.name || t('academy.unknownStaff', 'Unknown Staff')}</p>
                                <p className="text-sm text-muted-foreground">{course?.title || assignment.course_id}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              {assignment.due_date && (
                                <div className="text-right">
                                  <p className="text-xs text-muted-foreground">{t('academy.dueDate', 'Due Date')}</p>
                                  <p className={`text-sm font-medium ${isOverdue ? 'text-red-600' : isDueSoon ? 'text-amber-600' : ''}`}>
                                    {new Date(assignment.due_date).toLocaleDateString()}
                                  </p>
                                </div>
                              )}
                              <Badge className={
                                assignment.status === 'completed' ? 'bg-green-100 text-green-800' :
                                isOverdue ? 'bg-red-100 text-red-800' :
                                isDueSoon ? 'bg-amber-100 text-amber-800' :
                                'bg-blue-100 text-blue-800'
                              }>
                                {isOverdue ? t('academy.status.overdue', 'Overdue') : isDueSoon ? t('academy.status.dueSoon', 'Due Soon') : assignment.status}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Admin Analytics Tab */}
          {isAdmin && (
            <TabsContent value="analytics" className="space-y-6">
              <Card className="border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg"><BarChart3 className="w-6 h-6 text-blue-600" /></div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{t('academy.analytics.title', 'Staff Training Analytics')}</h3>
                        <p className="text-sm text-slate-600">{t('academy.analytics.subtitle', 'Monitor completion rates and track team progress')}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={handleProcessReminders} disabled={processingReminders}>
                        <Bell className="w-4 h-4 mr-2" />{processingReminders ? t('academy.processing', 'Processing...') : t('academy.sendReminders', 'Send Reminders')}
                      </Button>
                      <Button onClick={() => setShowAssignDialog(true)}>
                        <UserPlus className="w-4 h-4 mr-2" />{t('academy.assignTraining', 'Assign Training')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">{t('academy.analytics.totalCourses', 'Total Courses')}</p><p className="text-3xl font-bold">{totalCourses}</p></CardContent></Card>
                <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">{t('academy.analytics.totalCeus', 'Total CEUs Available')}</p><p className="text-3xl font-bold">{totalCEUsAvailable.toFixed(1)}</p></CardContent></Card>
                <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">{t('academy.analytics.requiredCourses', 'Required Courses')}</p><p className="text-3xl font-bold">{requiredCourses.length}</p></CardContent></Card>
                <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">{t('academy.analytics.trainingTracks', 'Training Tracks')}</p><p className="text-3xl font-bold">{Object.keys(TRAINING_COURSES).length}</p></CardContent></Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><PieChart className="w-5 h-5" />{t('academy.analytics.tracksOverview', 'Training Tracks Overview')}</CardTitle>
                  <CardDescription>{t('academy.analytics.tracksDesc', 'Courses and CEU credits by discipline')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(TRAINING_COURSES).map(([key, track]) => {
                      const trackCourses = track.courses || [];
                      const completedInTrack = trackCourses.filter(c => completedCourses.includes(c.id)).length;
                      const progressPercent = trackCourses.length > 0 ? (completedInTrack / trackCourses.length) * 100 : 0;
                      const TrackIcon = track.icon;
                      return (
                        <div key={key} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`p-1.5 rounded ${track.color} text-white`}><TrackIcon className="w-4 h-4" /></div>
                              <span className="font-medium">{track.title}</span>
                              {track.discipline && <Badge variant="outline" className="text-xs">{track.discipline}</Badge>}
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-muted-foreground">{trackCourses.length} {t('academy.courses', 'courses')}</span>
                              <span className="font-medium">{track.totalCEUs} CEUs</span>
                              <span className={progressPercent === 100 ? "text-green-600 font-medium" : "text-muted-foreground"}>{completedInTrack}/{trackCourses.length}</span>
                            </div>
                          </div>
                          <Progress value={progressPercent} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Dialogs */}
      <VideoDialog
        isOpen={showVideoDialog}
        onClose={() => setShowVideoDialog(false)}
        course={selectedCourse}
        isCompleted={selectedCourse ? completedCourses.includes(selectedCourse.id) : false}
        videoWatched={selectedCourse ? videoWatched[selectedCourse.id] : false}
        onMarkVideoWatched={markVideoWatched}
        onStartQuiz={startQuiz}
        onViewCertificate={openCertificate}
      />

      <QuizDialog
        isOpen={showQuizDialog}
        onClose={() => setShowQuizDialog(false)}
        course={selectedCourse}
        onComplete={markCourseComplete}
        onViewCertificate={openCertificate}
        onReviewVideo={openCourse}
      />

      <AssignmentDialog
        isOpen={showAssignDialog}
        onClose={() => setShowAssignDialog(false)}
        staffList={staffList}
        disciplines={disciplines.map(d => ({ key: d.key || d, title: d.title || d, totalCEUs: d.totalCEUs || 0 }))}
        onCreateAssignment={handleCreateAssignment}
        isLoading={assignmentLoading}
      />

      <Dialog open={showCertificateDialog} onOpenChange={setShowCertificateDialog}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{t('academy.certificate.title', 'Certificate of Completion')}</DialogTitle>
            <DialogDescription>{t('academy.certificate.subtitle', 'Print or download your certificate')}</DialogDescription>
          </DialogHeader>
          <div className="overflow-auto max-h-[70vh] flex justify-center py-4">
            {certificateCourse && (
              <CertificateTemplate
                user={user}
                courseTitle={certificateCourse.title}
                completionDate={completionDates[certificateCourse.id] || new Date().toISOString()}
                ceuCredits={certificateCourse.ceuCredits}
                isELNEC={certificateCourse.elnecModule || certificateCourse.isELNEC}
                certificateRef={certificateRef}
              />
            )}
          </div>
          <div className="flex justify-center gap-4">
            <Button onClick={printCertificate}><Printer className="w-4 h-4 mr-2" />{t('academy.printCertificate', 'Print Certificate')}</Button>
            <Button variant="outline" onClick={() => setShowCertificateDialog(false)}>{t('common.close', 'Close')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
